// @ts-nocheck
import * as React from 'react';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { fetchAdmissionLeadDetails, fetchAdmissionLeads } from '../console/services/leadsApi';

const AdmissionsLeadsContext = React.createContext(null);
const initialLoads = new Map();

export function AdmissionsLeadsProvider({ children }) {
  const authData = useAuth();
  const instituteId = authData?.INSTITUTE_ID;
  const branchId = authData?.BRANCH_ID;
  const [leads, setLeads] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [selectedLeadId, setSelectedLeadId] = React.useState('');
  const pendingDetails = React.useRef(new Map());

  const loadLeads = React.useCallback(async (force = false) => {
    setLoading(true);
    setError('');
    try {
      const cacheKey = `${instituteId}:${branchId}`;
      if (force) initialLoads.delete(cacheKey);
      if (!initialLoads.has(cacheKey)) {
        initialLoads.set(cacheKey, fetchAdmissionLeads({ INSTITUTE_ID: instituteId, BRANCH_ID: branchId })
          .catch((requestError) => { initialLoads.delete(cacheKey); throw requestError; }));
      }
      const items = await initialLoads.get(cacheKey);
      setLeads(items);
      setSelectedLeadId((current) => items.some((item) => item.id === current) ? current : (items[0]?.id || ''));
      return items;
    } catch (requestError) {
      setError(requestError?.response?.data?.message || requestError?.message || 'Unable to load admission leads.');
      throw requestError;
    } finally {
      setLoading(false);
    }
  }, [instituteId, branchId]);

  // Provider lives above admissions routes: this runs once per hard refresh/session identity.
  React.useEffect(() => {
    if (!instituteId || !branchId) return;
    loadLeads().catch(() => undefined);
  }, [instituteId, branchId, loadLeads]);

  const mergeLead = React.useCallback((id, details) => {
    setLeads((items) => items.map((item) => item.id === id ? { ...item, ...details } : item));
  }, []);

  const ensureLeadDetails = React.useCallback(async (id) => {
    const lead = leads.find((item) => item.id === id);
    if (!lead || lead.detailsLoaded) return lead;
    if (pendingDetails.current.has(id)) return pendingDetails.current.get(id);

    const request = fetchAdmissionLeadDetails(lead.leadId, {
      INSTITUTE_ID: instituteId,
      BRANCH_ID: branchId,
    }).then((details) => {
      const hydrated = { ...lead, ...details };
      mergeLead(id, details);
      return hydrated;
    }).finally(() => pendingDetails.current.delete(id));

    pendingDetails.current.set(id, request);
    return request;
  }, [leads, instituteId, branchId, mergeLead]);

  const value = React.useMemo(() => ({
    leads,
    loading,
    error,
    selectedLeadId,
    setSelectedLeadId,
    selectedLead: leads.find((item) => item.id === selectedLeadId) || null,
    ensureLeadDetails,
    mergeLead,
    refreshLeads: () => loadLeads(true),
  }), [leads, loading, error, selectedLeadId, ensureLeadDetails, mergeLead, loadLeads]);

  return <AdmissionsLeadsContext.Provider value={value}>{children}</AdmissionsLeadsContext.Provider>;
}

export function useAdmissionsLeads() {
  const value = React.useContext(AdmissionsLeadsContext);
  if (!value) throw new Error('useAdmissionsLeads must be used inside AdmissionsLeadsProvider.');
  return value;
}
